import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';
import {
  getSharedAuthToken,
  getSharedRefreshToken,
  setSharedAuthToken,
  clearSharedAuthTokens,
  isTokenExpired,
  decodeToken,
} from '@/lib/shared-auth';

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  /** Hub app-specific role from the apps JWT claim (e.g., 'org_admin', 'org_owner') */
  hubRole?: string;
  organizations?: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
    type: HubOrgType;
    /** Accounts org kind from the claim: SUPER | AGENCY | CLIENT. */
    kind?: string;
  }>;
}

/**
 * How the Hub classifies an org (mirrors the API's HubOrgType):
 * AGENCY = Solid Digital, PARTNER = a partner agency (kind AGENCY),
 * CLIENT = everyone else. PARTNER is neither of the others on purpose, so no
 * check written for Solid staff or for clients silently includes partners.
 */
export type HubOrgType = 'AGENCY' | 'PARTNER' | 'CLIENT';

export function orgTypeFor(orgId: string, kind?: string): HubOrgType {
  if (orgId === import.meta.env.VITE_PRIMARY_AGENCY_ORG_ID) return 'AGENCY';
  return kind === 'AGENCY' ? 'PARTNER' : 'CLIENT';
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  idToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;

  // Actions
  setAuth: (accessToken: string, user?: User) => void;
  logout: (options?: { skipOidcLogout?: boolean }) => void;
  setUser: (user: User) => void;
  updateAvatarUrl: (url: string | null) => void;
  setLoading: (loading: boolean) => void;
  setHasHydrated: (state: boolean) => void;
}

/**
 * Get the OIDC issuer URL.
 * Uses VITE_AUTH_URL env var, with smart fallback for production.
 */
function getOidcIssuerUrl(): string {
  // Use env var if set
  const envAuthUrl = import.meta.env.VITE_AUTH_URL;
  if (envAuthUrl) {
    return envAuthUrl;
  }

  // Production fallback: derive auth subdomain from current hostname
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    parts[0] = 'auth';
    return `https://${parts.join('.')}`;
  }

  // Local fallback
  return 'http://localhost:3001';
}

const APP_ROLE_MAP: Record<string, string> = { admin: 'org_admin', member: 'org_member', owner: 'org_owner' };

/**
 * Decode a JWT access token and build a User object from its claims.
 * Handles org role mapping, agency org detection, and hubRole extraction.
 */
function buildUserFromToken(token: string): User | null {
  const payload = decodeToken(token);
  if (!payload) return null;

  const agencyOrgId = import.meta.env.VITE_PRIMARY_AGENCY_ORG_ID;
  const hubAppClaim = (payload.apps as any[] || []).find((a: any) => a.client_id === 'solid-hub');
  const hubAppRole = hubAppClaim?.role?.toLowerCase();

  // Role hierarchy for comparing org vs app roles (higher = more privileged)
  const roleLevel: Record<string, number> = {
    'org_owner': 3, 'owner': 3,
    'org_admin': 2, 'admin': 2,
    'org_member': 1, 'member': 1,
  };

  const organizations = ((payload.orgs as any[]) || []).map((org: any) => {
    const isAgencyOrg = org.org_id === agencyOrgId;
    // Only use app role if it elevates the user (never downgrade org_owner to admin)
    const orgLevel = roleLevel[(org.role || '').toLowerCase()] || 0;
    const appLevel = hubAppRole ? (roleLevel[hubAppRole] || 0) : 0;
    const effectiveRole = isAgencyOrg && appLevel > orgLevel ? hubAppRole : org.role;
    const normalized = effectiveRole.toLowerCase();
    return {
      id: org.org_id,
      name: org.name,
      slug: org.slug,
      role: APP_ROLE_MAP[normalized] || normalized,
      type: orgTypeFor(org.org_id, org.kind),
      kind: org.kind,
    };
  });

  return {
    id: payload.sub as string,
    email: payload.email as string,
    name: payload.name as string,
    avatarUrl: payload.picture as string | undefined,
    hubRole: hubAppRole ? (APP_ROLE_MAP[hubAppRole] || hubAppRole) : undefined,
    organizations,
    apps: (payload.apps as any[]) || [],
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      idToken: null,
      isAuthenticated: false,
      isLoading: false,
      _hasHydrated: false,
      
      setAuth: (accessToken, user) => {
        set({
          accessToken,
          user: user || null,
          isAuthenticated: true,
          isLoading: false,
        });
        // Also set shared cookie for cross-subdomain SSO
        setSharedAuthToken(accessToken);
      },
      
      logout: (options?: { skipOidcLogout?: boolean }) => {
        console.log('[Auth] logout() called', options);

        // Clear all React Query cache to prevent stale user data
        queryClient.clear();

        // Clear auth storage (OIDC tokens). Both keys: the app-bar reads
        // `solid-auth` FIRST and falls back to `solid-hub-auth`, so leaving a
        // stale `solid-auth` behind lets an old token shadow the fresh one
        // written by the next sign-in.
        localStorage.removeItem('solid-hub-auth');
        localStorage.removeItem('solid-auth');
        sessionStorage.removeItem('oidc_code_verifier');
        sessionStorage.removeItem('oidc_state');

        // Clear shared auth cookies for cross-subdomain SSO
        clearSharedAuthTokens();

        set({
          user: null,
          accessToken: null,
          idToken: null,
          isAuthenticated: false,
          isLoading: false,
        });

        // If skipOidcLogout is true (e.g., session already expired), just reload the page
        // This will trigger the app-bar to start a fresh login flow
        if (options?.skipOidcLogout) {
          console.log('[Auth] Skipping OIDC logout, reloading to trigger fresh login');
          window.location.href = window.location.origin;
          return;
        }

        // Build OIDC logout URL using the centralized OIDC issuer URL
        const oidcIssuer = getOidcIssuerUrl();
        const logoutUrl = new URL(`${oidcIssuer}/oauth/logout`);
        logoutUrl.searchParams.set('client_id', 'solid-hub');
        logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin);

        console.log('[Auth] Logging out via OIDC:', logoutUrl.toString());
        window.location.href = logoutUrl.toString();
      },
      
      setUser: (user) => {
        set({ user });
      },

      updateAvatarUrl: (url) => {
        set((state) => ({
          user: state.user ? { ...state.user, avatarUrl: url ?? undefined } : null,
        }));
      },
      
      setLoading: (loading) => {
        set({ isLoading: loading });
      },
      
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'solid-hub-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        idToken: state.idToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && !state.accessToken) {
          // No local token — try shared cookie for cross-subdomain SSO
          const sharedToken = getSharedAuthToken();
          if (sharedToken && !isTokenExpired(sharedToken)) {
            console.log('[Auth] Found valid token in shared cookie, using it');
            const user = buildUserFromToken(sharedToken);
            state.setAuth(sharedToken, user || undefined);
          }
        } else if (state?.accessToken) {
          // Identity-consistency guard: the shared cookie is rewritten by
          // whichever app authenticated most recently, so it can belong to a
          // different user than this app's persisted session (e.g. someone
          // signed into a second account in another Solid app). Never keep a
          // stale identity: force a fresh login and let the auth server's
          // session cookie decide who the user is.
          const sharedToken = getSharedAuthToken();
          const localSub = state.user?.id || (decodeToken(state.accessToken)?.sub as string | undefined);
          if (sharedToken && !isTokenExpired(sharedToken)) {
            const sharedSub = decodeToken(sharedToken)?.sub as string | undefined;
            if (localSub && sharedSub && localSub !== sharedSub) {
              console.warn('[Auth] Shared cookie belongs to a different user; forcing fresh login');
              setTimeout(() => state.logout({ skipOidcLogout: true }), 0);
              state.setHasHydrated(true);
              return;
            }
          }

          if (state.user && !state.user.hubRole) {
            enrichSession(state);
          }
        }
        state?.setHasHydrated(true);
      },
    }
  )
);

/** Enrich a rehydrated session whose user is missing hubRole. */
function enrichSession(state: AuthState) {
  if (!state.accessToken) return;

  // Existing session missing hubRole — try to enrich from ID token
  // (Access tokens only have minimal claims; ID tokens have rich claims)
  const idToken = state.idToken;
  if (idToken) {
    console.log('[Auth] Enriching session from stored ID token');
    const user = buildUserFromToken(idToken);
    if (user) {
      state.setUser(user);
    }
  } else {
    // No ID token — try enriching from the access token itself.
    // In dev mode the access token carries all claims (sub, orgs, apps, etc.)
    // so this works correctly. In production the access token is opaque but
    // the ID token is always stored alongside it, so this branch only fires
    // in dev/local environments.
    console.log('[Auth] No ID token — enriching session from access token');
    const user = buildUserFromToken(state.accessToken);
    if (user) {
      state.setUser(user);
    } else {
      // Truly can't recover — force fresh login
      console.log('[Auth] Cannot decode access token, forcing fresh login');
      setTimeout(() => state.logout({ skipOidcLogout: true }), 0);
    }
  }
}

/**
 * Check if the current user is an agency admin or owner.
 * Checks both the org-level role and the app-level role for the Hub client.
 * The app role (from the `apps` JWT claim) is the primary source of truth for
 * Hub-specific permissions, while the org role is a fallback.
 */
export function getIsAgencyAdmin(user: User | null): boolean {
  if (!user) return false;

  // Agency authority requires membership in the AGENCY org. A role name from the
  // app/JWT claim must never confer agency access on its own — otherwise a
  // client-org owner/admin (or an "admin" Hub app grant on a client account)
  // would be treated as an agency admin and see every org and project.
  const agencyOrgId = import.meta.env.VITE_PRIMARY_AGENCY_ORG_ID;
  const orgs: any[] = user.organizations || (user as any).orgs || [];

  const agencyOrg = agencyOrgId
    ? orgs.find((org: any) => (org.id || org.org_id) === agencyOrgId)
    : orgs.find((org: any) => org.type === 'AGENCY');

  if (!agencyOrg) return false;

  // The user is in the agency org — an elevated app-level role counts.
  const apps: any[] = (user as any).apps || [];
  const hubApp = apps.find((a: any) => a.client_id === 'solid-hub');
  if (hubApp) {
    const appRole = (hubApp.role || '').toLowerCase();
    if (appRole === 'admin' || appRole === 'owner') return true;
  }
  if (user.hubRole) {
    const hr = user.hubRole.toLowerCase();
    if (hr === 'org_admin' || hr === 'org_owner' || hr === 'admin' || hr === 'owner') return true;
  }

  const role = (agencyOrg.role || '').toLowerCase();
  return role === 'org_owner' || role === 'org_admin' || role === 'admin' || role === 'owner';
}

/**
 * Check if the user is a system admin (agency owner).
 * System admins have override access to client-only resources.
 */
export function getIsSystemAdmin(user: User | null): boolean {
  if (!user) return false;

  // Like getIsAgencyAdmin: agency-owner authority requires membership in the
  // AGENCY org, so a client-org owner can never be treated as a system admin.
  const agencyOrgId = import.meta.env.VITE_PRIMARY_AGENCY_ORG_ID;
  const orgs: any[] = user.organizations || (user as any).orgs || [];

  const agencyOrg = agencyOrgId
    ? orgs.find((org: any) => (org.id || org.org_id) === agencyOrgId)
    : orgs.find((org: any) => org.type === 'AGENCY');

  if (!agencyOrg) return false;

  const apps: any[] = (user as any).apps || [];
  const hubApp = apps.find((a: any) => a.client_id === 'solid-hub');
  if (hubApp) {
    const appRole = (hubApp.role || '').toLowerCase();
    if (appRole === 'owner') return true;
  }
  if (user.hubRole) {
    const hr = user.hubRole.toLowerCase();
    if (hr === 'org_owner' || hr === 'owner') return true;
  }

  const role = (agencyOrg.role || '').toLowerCase();
  return role === 'org_owner' || role === 'owner' || agencyOrg.isOwner === true;
}

/**
 * Check if the user belongs to the agency org at ANY level (member/admin/owner).
 * Used to gate agency-only features like time tracking that aren't admin-only.
 */
/**
 * Partner agencies. A partner agency's admins run their own projects and
 * client orgs; its people are staff on those projects. None of the helpers
 * above (Solid-only, platform-wide) ever count them.
 */
function isAdminRole(role?: string): boolean {
  const r = (role || '').toLowerCase();
  return r === 'org_owner' || r === 'org_admin' || r === 'owner' || r === 'admin';
}

/** The partner agency org this user owns or administers, if any. */
export function getManagedPartnerAgency(user: User | null) {
  return user?.organizations?.find((org) => org.type === 'PARTNER' && isAdminRole(org.role));
}

/** Who may start a project: Solid admins, and a partner agency's admins (for their agency). */
export function getCanCreateProjects(user: User | null): boolean {
  return getIsAgencyAdmin(user) || !!getManagedPartnerAgency(user);
}

/**
 * Admin of THIS project (settings, members, archive, links…): Solid admins on
 * every project, a partner agency's admins on projects their agency owns.
 * `projectOrgId` is the project's owning org (`project.organizationId`).
 */
export function getIsProjectAdmin(user: User | null, projectOrgId?: string | null): boolean {
  if (getIsAgencyAdmin(user)) return true;
  if (!user || !projectOrgId) return false;
  return !!user.organizations?.some(
    (org) => org.type === 'PARTNER' && org.id === projectOrgId && isAdminRole(org.role),
  );
}

/**
 * Staff on THIS project (agency-only lists, staff views): Solid staff on every
 * project, a partner agency's people on projects their agency owns.
 */
export function getIsProjectStaff(user: User | null, projectOrgId?: string | null): boolean {
  if (getIsAgencyMember(user)) return true;
  if (!user || !projectOrgId) return false;
  return !!user.organizations?.some((org) => org.type === 'PARTNER' && org.id === projectOrgId);
}

export function getIsAgencyMember(user: User | null): boolean {
  if (!user) return false;
  // Admins/owners are always agency members (covers app-role-only cases where
  // the org list isn't tagged with type AGENCY in local/dev tokens).
  if (getIsAgencyAdmin(user)) return true;

  const agencyOrgId = import.meta.env.VITE_PRIMARY_AGENCY_ORG_ID;
  const orgs: any[] = user.organizations || (user as any).orgs || [];
  return agencyOrgId
    ? orgs.some((org: any) => (org.id || org.org_id) === agencyOrgId)
    : orgs.some((org: any) => org.type === 'AGENCY');
}
